import { FlexColumn, FuuzeButton, FuuzeContainer } from '@fuuze/shared';
import { wrapCreateBrowserRouterV6 } from '@sentry/react';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BiChevronRight } from 'react-icons/bi';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { FuuzeLink } from './components/fuuze-link';
import { IsUserCreatorRouteGuard } from './components/Guards/IsUserCreatorRouteGuard';
import RedirectUserGuard from './components/Guards/redirect-user.guard';
import { UserLoggedGuard } from './components/Guards/user-logged-guard';
import PageContainer from './pages/page-container';
import { PageRoot } from './pages/page-root';

// Auth pages
const LoginPage = lazy(() => import('./pages/Auth/login-page'));
const RegisterPage = lazy(() => import('./pages/Auth/register-page'));
const AuthActionPage = lazy(() => import('./pages/Auth/auth-action-page'));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/forgot-password-page'));
const ConfirmEmailPage = lazy(() => import('./pages/Auth/ConfirmEmailPage'));
const ResetPasswordPageFirebase = lazy(() => import('./pages/Auth/reset-password-page-firebase'));
const AccountUnderDeletionPage = lazy(() => import('./pages/Auth/account-under-deletion-page'));

// Podcast pages
const SearchPage = lazy(() => import('./pages/Search/search-page'));
const GenrePage = lazy(() => import('./pages/genre/genre-page'));
const PodcastDetailPage = lazy(() => import('./pages/podcast-detail/podcast-detail-page'));
const TiersPage = lazy(() => import('./pages/Tiers/tiers-page'));
const PodcastEpisodeDetailPage = lazy(() => import('./pages/podcast-episode-detail/podcast-episode-detail-page'));

// Library pages
const LibraryPage = lazy(() => import('./pages/Library/library-page'));
const LibraryRecentReleases = lazy(() => import('./pages/Library/latest-releases/library-latest-releases.page'));

// Others
const BecomeCreatorPage = lazy(() => import('./pages/BecomeCreator/become-creator-page'));
const ConfirmPurchasePage = lazy(() => import('./pages/ConfirmPurchase/confirm-purchase-page'));

// Creator pages
const ManageTiersPage = lazy(() => import('./pages/Creator/Tiers/manage-tiers-page'));
const EditTierPage = lazy(() => import('./pages/Creator/EditTier/edit-tier.page'));
const ManagePodcastPage = lazy(() => import('./pages/Creator/ManagePodcast/manage-podcast-page'));
const DashboardPage = lazy(() => import('./pages/Creator/Dashboard/dashboard-page'));
const StudioHomePage = lazy(() => import('./pages/Creator/studio/home/studio-home-page'));
const StudioPage = lazy(() => import('./pages/Creator/studio/studio/studio-page'));

// Settings pages
const SettingsPage = lazy(() => import('./pages/Settings/settings-page'));
const SettingsGeneralOutlet = lazy(() => import('./pages/Settings/General/settings-general-outlet'));
const SettingsSubscriptionOutlet = lazy(() => import('./pages/Settings/Subscriptions/settings-subscriptions-outlet'));
const SettingsPaymentsOutlet = lazy(() => import('./pages/Settings/Payments/settings-payments-outlet'));

const NotFoundPage = lazy(() => import('./pages/404'));
const Fallback = () => <FlexColumn style={{ alignItems: 'center', justifyContent: 'center', height: '100dvh' }} />;

const PageLoader = (Page: ReturnType<typeof lazy>) => {
  return (
    <Suspense fallback={<Fallback />}>
      <Page />
    </Suspense>
  );
};

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  [
    {
      path: '/',
      errorElement: <RootErrorBoundary />,
      element: <PageRoot />,
      children: [
        { path: '/login', element: PageLoader(LoginPage) },
        { path: '/register', element: PageLoader(RegisterPage) },
        { path: '/forgotPassword', element: PageLoader(ForgotPasswordPage) },
        { path: '/confirm-email', element: PageLoader(ConfirmEmailPage) },
        { path: '/reset-password', element: PageLoader(ResetPasswordPageFirebase) },
        { path: '/account-under-cancellation', element: PageLoader(AccountUnderDeletionPage) },
        { path: '/auth-action', element: PageLoader(AuthActionPage) },

        {
          path: '/',
          element: (
            <RedirectUserGuard>
              <PageContainer />
            </RedirectUserGuard>
          ),
          children: [
            { path: '/', element: <Navigate to='/search' replace={true} /> },
            { path: '/search', element: PageLoader(SearchPage) },
            { path: '/genre/:genre', element: PageLoader(GenrePage) },
            {
              path: '/podcast',
              children: [
                { path: ':channelSlug', element: PageLoader(PodcastDetailPage) },
                { path: ':channelSlug/tiers', element: PageLoader(TiersPage) },
                { path: ':channelSlug/episode/:itemSlug', element: PageLoader(PodcastEpisodeDetailPage) },
                { index: true, path: '*', element: <Navigate to='/' replace={true} /> },
              ],
            },
            {
              path: 'library',
              children: [
                { index: true, element: PageLoader(LibraryPage) },
                { path: 'releases', element: PageLoader(LibraryRecentReleases) },
              ],
            },
            { path: 'becomeCreator', element: PageLoader(BecomeCreatorPage) },
            { path: 'confirmPurchase', element: PageLoader(ConfirmPurchasePage) },
            {
              path: '/creator',
              element: (
                <IsUserCreatorRouteGuard>
                  <Outlet />
                </IsUserCreatorRouteGuard>
              ),
              children: [
                { index: true, element: <Navigate to='dashboard' replace={true} /> },
                { path: 'podcast', element: PageLoader(ManagePodcastPage) },
                {
                  path: 'tiers/:tierId',
                  element: PageLoader(EditTierPage),
                },
                {
                  path: 'tiers',
                  element: PageLoader(ManageTiersPage),
                },
                { path: 'dashboard', element: PageLoader(DashboardPage) },
                {
                  path: 'studio',
                  children: [
                    { path: ':itemId', element: PageLoader(StudioPage) },
                    { index: true, element: PageLoader(StudioHomePage) },
                  ],
                },
              ],
            },
            {
              path: '/settings',
              element: <UserLoggedGuard>{PageLoader(SettingsPage)}</UserLoggedGuard>,
              children: [
                { index: true, element: <Navigate to='general' replace={true} /> },
                { path: 'general', element: PageLoader(SettingsGeneralOutlet) },
                { path: 'subscriptions', element: PageLoader(SettingsSubscriptionOutlet) },
                { path: 'payments', element: PageLoader(SettingsPaymentsOutlet) },
              ],
            },
          ],
        },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
  ],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

// Reload the page when vite cannot load a dynamic import
// @see: https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

function RootErrorBoundary() {
  const { t } = useTranslation('translation', { keyPrefix: 'components.rootErrorBoundary' });

  return (
    <FuuzeContainer style={{ alignItems: 'center', justifyContent: 'center', height: '100dvh' }}>
      <h1>{t('title')}</h1>
      <p style={{ textAlign: 'center' }}>{t('description')}</p>
      <FuuzeLink to='/'>
        <FuuzeButton flat style={{ display: 'flex', alignItems: 'center' }}>
          <span>{t('cta')}</span>
          <BiChevronRight size={32} />
        </FuuzeButton>
      </FuuzeLink>
    </FuuzeContainer>
  );
}
